import React, { forwardRef, useImperativeHandle, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import styled from 'styled-components'
import toast from 'react-hot-toast'

import P from '../../../components/typography/P'

import PersonalInformation from './PersonalInformation'
import EducationalBackgrounds from './EducationalBackgrounds'
import JobExperiences from './JobExperiences'
import Skills from './Skills'
import CharacterReferences from './CharacterReferences'

import apiClient from '../../../helpers/apiClient'

import { debounce } from '../../../utils/util'

const ReviewCV = forwardRef((props, ref) => {
  const {
    firstName,
    middleName,
    lastName,
    professionalSummary,
    cvImageLink,
    contactNumber,
    country,
    city,
    address,
    zipCode,
    basis,
    minSalary,
    maxSalary,
    currencies,
    cvImageFile,
    educationalBackground,
    experiences,
    professionalHeadlines,
    characterReferences,
    technicalSkills,
    softSkills,
  } = useSelector((state) => state.applicant)

  const history = useHistory()

  const handleSubmit = useCallback(
    debounce(() => {
      const toastId = toast.loading('Updating your CV...')

      const formData = new FormData()

      formData.append('first_name', firstName)
      formData.append('middle_name', middleName)
      formData.append('last_name', lastName)
      formData.append('name', `${firstName} ${middleName} ${lastName}`)
      formData.append('salary_basis', basis.value)
      formData.append('min_salary', minSalary)
      formData.append('max_salary', maxSalary)
      formData.append('currencies', currencies.value)
      formData.append(
        'professional_headline',
        JSON.stringify(professionalHeadlines)
      )
      formData.append('professional_summary', professionalSummary)

      const newMeta = {
        ZIPCode: zipCode,
        educationalData: educationalBackground,
        experienceState: experiences,
        fullSkills: [technicalSkills, softSkills],
        streetAddress: address,
      }
      formData.append('meta', JSON.stringify(newMeta))

      formData.append(
        'character_reference',
        JSON.stringify(characterReferences)
      )
      formData.append('cv_image_url', cvImageLink)
      formData.append('city', city.value)
      formData.append('country', country.value)
      formData.append('phone_number', contactNumber)

      if (cvImageFile) {
        formData.append('cvImage', cvImageFile)
      }

      apiClient
        .post('/applicant/cv', formData)
        .then((res) => {
          if (res.status === 201) {
            toast.success('Your CV was successfully updated!')
            setTimeout(() => {
              history.push('/remote-setup')
            }, 3000) // 3 seconds
          }
        })
        .catch((err) => {
          console.log(err)
          toast.error('Something went wrong. Please try again.')
        })
        .finally(() => toast.dismiss(toastId))
    }, 800),
    [
      firstName,
      middleName,
      lastName,
      basis,
      minSalary,
      maxSalary,
      currencies,
      professionalHeadlines,
      professionalSummary,
      zipCode,
      educationalBackground,
      experiences,
      technicalSkills,
      softSkills,
      address,
      characterReferences,
      cvImageLink,
      city,
      country,
      contactNumber,
      cvImageFile,
    ]
  )

  useImperativeHandle(ref, () => ({
    onSubmit: handleSubmit,
  }))

  return (
    <StyledReviewCV>
      <P size="1.5rem" weight="500" mb="0.25rem">
        Review your CV
      </P>

      <P color="#6F6F6F">
        Take a moment to review your CV. Confirm that all details are correct
        before submitting.
      </P>

      <SectionsWrapper>
        <Section>
          <PersonalInformation />
        </Section>

        <Section>
          <EducationalBackgrounds />
        </Section>

        <Section>
          <JobExperiences />
        </Section>

        <Section>
          <Skills />
        </Section>

        <Section>
          <CharacterReferences />
        </Section>
      </SectionsWrapper>
    </StyledReviewCV>
  )
})

const StyledReviewCV = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid rgb(234, 234, 248);
`

ReviewCV.displayName = 'ReviewCV'

export default ReviewCV
